import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../Icon';
import classnames from 'classnames';
import { Field } from '@radix-ui/react-form';
import React from 'react';
import Text from '@/components/cms/Text';
import ProgressBar from '../ProgressBar';

export interface BlobData {
  name: string;
  blobUri: string;
  uploadDate: string;
}

export interface FileUploadFieldProps {
  onChange?: (file: File[]) => void;
  onClick?: () => void;
  validateFiles?: (file: File[]) => string[];
  onDelete?: (name: string) => void;
  onDownload?: (blobName: string, fileName?: string) => void;
  initialBlobFileData?: BlobData[];
  acceptedMimeTypes?: string[];
  name: string;
  isUploading?: boolean;
  isDownloading?: boolean;
  isDeleting?: boolean;
  didError?: boolean;
  useProgressBar?: boolean;
}

const FileUploadField: FC<FileUploadFieldProps> = ({
  onChange,
  onClick,
  validateFiles,
  onDelete,
  onDownload,
  initialBlobFileData,
  name,
  acceptedMimeTypes,
  isDeleting,
  isDownloading,
  isUploading,
  useProgressBar
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [userIsDragging, setUserIsDragging] = useState<boolean>();
  const [progress, setProgress] = useState<number>(0);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>();

  useEffect(() => {
    if (useProgressBar && (isUploading || isDeleting) && progress < 100) {
      const intervalId = setInterval(() => {
        setProgress(prev => prev + 1);
      }, 5);
      return () => clearInterval(intervalId);
    }
  }, [isDeleting, isUploading, progress, useProgressBar]);

  useEffect(() => {
    if (useProgressBar && !isUploading && !isDeleting && progress > 0) {
      setProgress(0);
    }
  }, [isDeleting, isUploading, progress, useProgressBar]);

  useEffect(() => {
    setButtonsDisabled(!!isDeleting || !!isDownloading);
  }, [isDeleting, isDownloading]);

  const isInitialBlobDataPopulated = useMemo(() => {
    return (initialBlobFileData?.length ?? 0) > 0;
  }, [initialBlobFileData?.length]);

  const fileArray = useCallback((files: FileList) => Array.from(files), []);

  const validateUploadedFiles = useCallback(
    (files: File[]) => {
      if (validateFiles) {
        const res = validateFiles(files);
        return !res?.length;
      }
    },
    [validateFiles]
  );

  const sanitizeFileNames = useCallback(
    (files: File[]) =>
      files?.map(a => {
        return new File([a], a.name, { type: a.type });
      }),
    []
  );

  const onFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target?.files?.length) return;

      const _files = sanitizeFileNames(fileArray(e.target?.files));

      if (validateUploadedFiles(_files) && onChange) {
        setFiles(_files);
        onChange(_files);
      }
    },
    [fileArray, onChange, sanitizeFileNames, validateUploadedFiles]
  );

  const onFileInputClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      if (e.currentTarget) {
        // reset file list array on input click. old files and values can get stuck.
        e.currentTarget.files = null;
        e.currentTarget.value = '';
      }

      onClick && onClick();
    },
    [onClick]
  );

  const preventDefaultBrowser = useCallback(
    <T extends React.UIEvent<Y>, Y extends HTMLElement>(e: T) => {
      e.stopPropagation();
      e.preventDefault();
    },
    []
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      preventDefaultBrowser(e);

      const _files = sanitizeFileNames(fileArray(e.dataTransfer?.files));
      const allFiles = [...files, ..._files];

      if (validateUploadedFiles(allFiles)) {
        if (!isInitialBlobDataPopulated) {
          setFiles(allFiles);
          onChange && onChange(allFiles);
        }
        setUserIsDragging(false);
      }
    },
    [
      preventDefaultBrowser,
      sanitizeFileNames,
      fileArray,
      validateUploadedFiles,
      isInitialBlobDataPopulated,
      files,
      onChange
    ]
  );

  const handleDownload = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>, name?: string) => {
      preventDefaultBrowser(e);

      if (initialBlobFileData?.length && name) {
        const blob = initialBlobFileData.find(a => a.name == name);

        blob?.blobUri && onDownload && onDownload(blob.blobUri, blob.name);
      }
    },
    [initialBlobFileData, onDownload, preventDefaultBrowser]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, name: string) => {
      preventDefaultBrowser(e);

      setFiles(files?.filter(a => a.name != name));

      const blob = initialBlobFileData?.find(a => a.name == name);
      onDelete && blob && onDelete(blob.blobUri);
    },
    [files, initialBlobFileData, onDelete, preventDefaultBrowser]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isInitialBlobDataPopulated) {
        setUserIsDragging(true);
      }
    },
    [isInitialBlobDataPopulated]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      preventDefaultBrowser(e);
    },
    [preventDefaultBrowser]
  );

  const handleDragExit = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      preventDefaultBrowser(e);
      setUserIsDragging(false);
    },
    [preventDefaultBrowser]
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      preventDefaultBrowser(e);

      if (
        fileInputRef?.current &&
        (e.key === ' ' || e.key === 'Enter') &&
        !e.shiftKey &&
        !e.altKey &&
        !e.ctrlKey
      )
        fileInputRef.current?.click();
    },
    [preventDefaultBrowser]
  );

  const emptyState = useMemo(() => {
    const labelClasses = classnames(
      'flex w-full cursor-pointer flex-col items-center justify-center rounded-md border border-gray hover:border hover:border-black active:opacity-30'
    );
    const containerClasses = classnames('flex flex-col items-center justify-center align-middle', {
      'opacity-70': userIsDragging
    });
    return (
      <Field name={name} className="w-full py-4">
        <div className={containerClasses}>
          <div onKeyUp={onKeyUp}>
            <label className={labelClasses} tabIndex={0} role="link">
              <input
                className="absolute size-px overflow-hidden border-0 p-0"
                type="file"
                accept={acceptedMimeTypes?.join(',')}
                tabIndex={-1}
                onChange={onFileChange}
                onClick={onFileInputClick}
                ref={fileInputRef}
              />
              {
                <span className="hover: flex flex-row items-center justify-center px-7 py-2 align-middle">
                  <Icon name="upload" size="large" className="my-auto" />
                  <Text element="span" className="text-1 ml-2 font-normal">
                    Select a file
                  </Text>
                </span>
              }
            </label>
          </div>
          <Text element="span" className="text-2 mt-2">
            Or drop your file here
          </Text>
        </div>
      </Field>
    );
  }, [acceptedMimeTypes, name, onFileChange, onFileInputClick, onKeyUp, userIsDragging]);

  const populatedState = useMemo(() => {
    const buttonClasses = classnames({
      'pointer-events-none opacity-50': buttonsDisabled
    });
    return (
      <div className="w-full py-4">
        {initialBlobFileData &&
          initialBlobFileData?.map((blob, i) => {
            const classes = classnames('flex w-full flex-row items-center justify-between px-3', {
              'mt-2': i != 0 && i != files?.length - 1
            });

            return (
              <div className={classes} key={`file-${i}`}>
                <div className="flex w-full flex-row items-center justify-start">
                  <Icon size="xx-large" name="file" className="my-auto basis-1/6" />
                  <div className="flex basis-auto flex-row flex-nowrap items-center justify-start align-middle">
                    <Text element="span" className="w-full">
                      {blob?.name?.length > 30
                        ? `${blob?.name?.substring(0, 29).trim()}...${blob?.name?.substring(blob?.name?.length - 3)}`
                        : blob?.name}
                    </Text>
                  </div>
                </div>
                <div className="flex basis-auto flex-row flex-nowrap items-center justify-between">
                  <button
                    className={`${buttonClasses} mr-3`}
                    onClick={e => handleDownload(e, blob?.name)}
                    type="button"
                    disabled={buttonsDisabled}
                  >
                    <Icon name="download" />
                  </button>
                  <button
                    className={buttonClasses}
                    onClick={e => handleDelete(e, blob?.name)}
                    type="button"
                    disabled={buttonsDisabled}
                  >
                    <Icon name="delete" />
                  </button>
                </div>
              </div>
            );
          })}
      </div>
    );
  }, [buttonsDisabled, initialBlobFileData, files?.length, handleDownload, handleDelete]);

  const renderProgressBar = useMemo(() => {
    if (isUploading) return <ProgressBar label="Uploading" progressPercent={progress} />;
    if (isDeleting) return <ProgressBar label="Deleting" progressPercent={progress} />;
  }, [isDeleting, isUploading, progress]);

  return (
    <div
      className="relative flex min-h-[91px] w-full flex-row items-center justify-center rounded-lg border border-gray bg-gray-lightest"
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragExit={handleDragExit}
      onDragLeave={handleDragExit}
      onDragOver={handleDragOver}
    >
      {useProgressBar && (!!isDeleting || !!isUploading)
        ? renderProgressBar
        : !initialBlobFileData?.length
          ? emptyState
          : populatedState}
    </div>
  );
};
export default FileUploadField;
